import { useState, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useGroups } from '../../api/hooks/useGroups';
import { useAddOrganizationsToGroup } from '../../api/hooks/useGroupsOrganization';
import { useOrganization } from '../../contexts/OrganizationContext';
import { GroupDto, GroupQueryParams } from '../../api/types/dtos/group';
import styles from './GroupSelectionModal.module.scss';
import { useSnackbar } from 'notistack';

interface GroupSelectionModalProps {
  open: boolean;
  onClose: () => void;
  selectedOrganizationIds: string[];
}

export const GroupSelectionModal: React.FC<GroupSelectionModalProps> = ({
  open,
  onClose,
  selectedOrganizationIds,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<GroupDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { selectedOrganization } = useOrganization();
  const { data: groups, isLoading: isLoadingGroups } = useGroups(
    selectedOrganization?.id || '',
    {
      isActive: true,
      sortBy: 'name',
      sortDirection: 'ASC',
    } as GroupQueryParams
  );

  const { enqueueSnackbar } = useSnackbar();
  const addToGroupMutation = useAddOrganizationsToGroup();

  const handleGroupChange = (_event: any, value: GroupDto | null) => {
    console.log('Selected group:', value);
    setSelectedGroup(value);
    setError(null);
  };

  const handleSubmit = useCallback(async () => {
    if (!selectedGroup?.id || !selectedOrganization?.id) {
      setError('Please select a group first');
      return;
    }

    try {
      await addToGroupMutation.mutateAsync({
        groupId: selectedGroup.id,
        organizationIds: selectedOrganizationIds
      });

      enqueueSnackbar(`Successfully added ${selectedOrganizationIds.length} organization(s) to group ${selectedGroup.name}`, {
        variant: 'success'
      });
      onClose();
    } catch (err) {
      setError('Failed to add organizations to group. Please try again.');
    }
  }, [selectedGroup, selectedOrganization?.id, selectedOrganizationIds, addToGroupMutation, onClose, enqueueSnackbar]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Organizations to Group</DialogTitle>
      <DialogContent>
        <Box className={styles.content}>
          <p className={styles.selectionCount}>
            {selectedOrganizationIds.length} organization{selectedOrganizationIds.length !== 1 ? 's' : ''} selected
          </p>

          <Autocomplete
            value={selectedGroup}
            onChange={handleGroupChange}
            options={groups || []}
            getOptionLabel={(option: GroupDto) => option.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={isLoadingGroups}
            noOptionsText={!groups?.length ? "No groups available" : "No matches found"}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Group"
                variant="outlined"
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingGroups ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {selectedGroup && (
            <Box className={styles.groupDetails}>
              <h3>Group Details</h3>
              <p><strong>Name:</strong> {selectedGroup.name}</p>
              <p><strong>Description:</strong> {selectedGroup.description}</p>
            </Box>
          )}

          {error && (
            <Alert severity="error" className={styles.error}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!selectedGroup || addToGroupMutation.isPending}
        >
          {addToGroupMutation.isPending ? (
            <>
              Adding... <CircularProgress size={20} className={styles.buttonProgress} />
            </>
          ) : (
            'Add to Group'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 